import requests from 'query/http'
import { getLoggedinUser, updateLoggedinUser } from '../helpers/api_helper'
import realtimeLogger from './realtimeLogger'

// 5 minute buffer before token expiration (as per requirements)
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000 // 5 minutes
const SAFETY_WINDOW_MS = 15_000

// Track token refresh state
let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null
let refreshTimer: NodeJS.Timeout | null = null

// Listeners for token updates (to notify active connections)
type TokenUpdateListener = (newToken: string) => void
const tokenUpdateListeners = new Set<TokenUpdateListener>()

const isTokenValid = (expiresAt?: string): boolean => {
  if (!expiresAt) return false
  const expires = Date.parse(expiresAt)
  if (Number.isNaN(expires)) return false
  return expires - Date.now() > SAFETY_WINDOW_MS
}

const isTokenExpiringSoon = (expiresAt?: string): boolean => {
  if (!expiresAt) return true
  const expires = Date.parse(expiresAt)
  if (Number.isNaN(expires)) return true
  const timeUntilExpiry = expires - Date.now()
  return timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS
}

const storeRealtimeToken = (tokenPayload: Record<string, any>) => {
  updateLoggedinUser({
    realtime_token: tokenPayload.realtime_token,
    realtime_token_expires_at: tokenPayload.realtime_token_expires_at,
    realtime_token_expires_in: tokenPayload.realtime_token_expires_in,
  })
}

/**
 * Schedule automatic token refresh before expiration
 */
const scheduleTokenRefresh = (expiresAt?: string): void => {
  // Clear existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }

  if (!expiresAt) return

  const expires = Date.parse(expiresAt)
  if (Number.isNaN(expires)) return

  // Calculate when to refresh (5 minutes before expiration)
  const timeUntilExpiry = expires - Date.now()
  const refreshDelay = Math.max(0, timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS)

  console.log(
    `[realtimeAuth] Scheduling token refresh in ${Math.round(refreshDelay / 1000)}s (${Math.round(timeUntilExpiry / 1000)}s until expiry)`
  )

  refreshTimer = setTimeout(async () => {
    console.log('[realtimeAuth] Automatic token refresh triggered')
    realtimeLogger.logTokenRefreshAttempt('Scheduled refresh before expiration')
    try {
      const newToken = await refreshRealtimeToken()
      if (newToken) {
        console.log('[realtimeAuth] Automatic token refresh successful')
        realtimeLogger.logTokenRefreshSuccess()
        notifyTokenUpdate(newToken)
      } else {
        console.error('[realtimeAuth] Automatic token refresh failed')
        realtimeLogger.logTokenRefreshFailure('Token refresh returned null')
      }
    } catch (error) {
      console.error('[realtimeAuth] Automatic token refresh error:', error)
      realtimeLogger.logTokenRefreshFailure(error as Error)
    }
  }, refreshDelay)
}

/**
 * Notify all listeners of token update
 */
const notifyTokenUpdate = (newToken: string) => {
  console.log(`[realtimeAuth] Notifying ${tokenUpdateListeners.size} listeners of token update`)
  realtimeLogger.logTokenUpdate(tokenUpdateListeners.size)
  const listenerArray = Array.from(tokenUpdateListeners)
  for (const listener of listenerArray) {
    try {
      listener(newToken)
    } catch (error) {
      console.error('[realtimeAuth] Token update listener error:', error)
      realtimeLogger.logEventError('token-update', 'token:updated', error as Error)
    }
  }
}

/**
 * Subscribe to token updates
 * @param listener - Callback function that receives the new token
 * @returns Unsubscribe function
 */
export const subscribeToTokenUpdates = (listener: TokenUpdateListener): (() => void) => {
  tokenUpdateListeners.add(listener)
  console.log(`[realtimeAuth] Token update listener added (total: ${tokenUpdateListeners.size})`)

  return () => {
    tokenUpdateListeners.delete(listener)
    console.log(`[realtimeAuth] Token update listener removed (total: ${tokenUpdateListeners.size})`)
  }
}

/**
 * Fetch a new realtime token from the backend
 */
export const fetchRealtimeToken = async (): Promise<string | null> => {
  try {
    console.log('[realtimeAuth] Fetching realtime token from /api/realtime/token')
    // requests interceptor unwraps response.data automatically (see api_helper.ts line 35)
    const data: any = await requests.query({ url: '/api/realtime/token', method: 'GET' })
    console.log('[realtimeAuth] Response:', data)

    const { realtime_token, realtime_token_expires_at, realtime_token_expires_in } =
      data ?? {}

    if (realtime_token) {
      console.log('[realtimeAuth] Got valid realtime token, expires at:', realtime_token_expires_at)
      storeRealtimeToken({
        realtime_token,
        realtime_token_expires_at,
        realtime_token_expires_in,
      })

      // Schedule automatic refresh before expiration
      scheduleTokenRefresh(realtime_token_expires_at)

      return realtime_token
    }

    console.warn('[realtimeAuth] No realtime_token in response:', data)
  } catch (error) {
    console.error('[realtimeAuth] Unable to refresh realtime token', error)
  }

  // Don't fall back to regular auth token - it's not a valid Supabase JWT
  return null
}

/**
 * Refresh the realtime token
 * Handles concurrent refresh requests by returning the same promise
 */
export const refreshRealtimeToken = async (): Promise<string | null> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    console.log('[realtimeAuth] Token refresh already in progress, waiting...')
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const newToken = await fetchRealtimeToken()
      return newToken
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Get a valid realtime access token
 * Automatically refreshes if token is expiring soon
 */
export const getRealtimeAccessToken = async (): Promise<string | null> => {
  const auth = getLoggedinUser()

  // If user is not authenticated, don't attempt to fetch token
  if (!auth || !auth.token) {
    console.log('[realtimeAuth] User not authenticated, skipping token fetch')
    return null
  }

  // Check if token is valid and not expiring soon
  if (auth?.realtime_token && isTokenValid(auth.realtime_token_expires_at)) {
    // Check if token is expiring soon and needs refresh
    if (isTokenExpiringSoon(auth.realtime_token_expires_at)) {
      console.log('[realtimeAuth] Token expiring soon, refreshing...')
      realtimeLogger.logTokenRefreshAttempt('Token expiring soon')
      const refreshed = await refreshRealtimeToken()
      if (refreshed) {
        console.log('[realtimeAuth] Token refreshed successfully')
        realtimeLogger.logTokenRefreshSuccess(auth.realtime_token_expires_at)
        notifyTokenUpdate(refreshed)
        return refreshed
      } else {
        console.warn('[realtimeAuth] Token refresh failed, using existing token')
        realtimeLogger.logTokenRefreshFailure('Token refresh returned null, using existing token')
        // Return existing token as fallback if refresh fails
        return auth.realtime_token
      }
    }

    console.log('[realtimeAuth] Using cached realtime token')
    return auth.realtime_token
  }

  console.log('[realtimeAuth] No valid cached token, fetching new one')
  realtimeLogger.logTokenRefreshAttempt('No valid cached token')
  const refreshed = await refreshRealtimeToken()
  if (refreshed) {
    realtimeLogger.logTokenRefreshSuccess()
    notifyTokenUpdate(refreshed)
    return refreshed
  }

  console.error('[realtimeAuth] No valid realtime token available')
  realtimeLogger.logTokenRefreshFailure('No valid realtime token available')
  // Don't fall back to regular auth token - it's not a valid Supabase JWT
  return null
}

/**
 * Cancel scheduled token refresh (useful on logout)
 */
export const cancelTokenRefresh = (): void => {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
    console.log('[realtimeAuth] Token refresh timer cancelled')
  }
}

/**
 * Clear all token update listeners
 */
export const clearTokenUpdateListeners = (): void => {
  tokenUpdateListeners.clear()
  console.log('[realtimeAuth] All token update listeners cleared')
}
